import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { analyticsApiService, type DocumentAnalytics } from '../../services/analyticsApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../../styles/theme'
import { capitalize, fmtDateTime, fmtNum, surfaceLabel } from '../../utils/analyticsFormat'
import { EmptyState, ErrorNote, VIEWS_EMPTY_TEXT } from './AnalyticsUi'

export default function DocumentViewsDrawer({ renderJobId, onClose }: { renderJobId: string; onClose: () => void }) {
  const [data, setData] = useState<DocumentAnalytics | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    setData(null); setError('')
    analyticsApiService.getDocument(renderJobId)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setError(e instanceof ApiError ? e.message : 'Failed to load document views') })
    return () => { live = false }
  }, [renderJobId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const stat = (label: string, value: string) => (
    <div style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm }}>
      <div style={{ fontSize: fontSize['2xs'], color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: fontSize.md, fontWeight: 700, color: colors.text }}>{value}</div>
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, background: colors.overlay, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Document views"
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(480px, 100vw)', height: '100%', background: colors.surface, boxShadow: shadow.xl,
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: fontSize.md, color: colors.text }}>Document views</div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, fontFamily: fontFamily.mono, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {renderJobId}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: fontSize.xl, cursor: 'pointer', color: colors.textSecondary, lineHeight: 1 }}>
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: spacing.md }}>
          {error && <ErrorNote message={error} />}
          {!data && !error && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading…</div>}
          {data && (
            <>
              <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
                {data.templateName && data.templateId
                  ? <Link to={`/app/templates/${data.templateId}?tab=analytics`} style={{ color: colors.primary, fontWeight: 500 }}>{data.templateName}</Link>
                  : data.templateName ?? 'Document'}
                {' · '}rendered {fmtDateTime(data.createdAt)}{data.pages != null ? ` · ${fmtNum(data.pages)} pages` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.sm, marginBottom: spacing.sm }}>
                {stat('Views', fmtNum(data.views))}
                {stat('Downloads', fmtNum(data.downloads))}
                {stat('Unique viewers', fmtNum(data.uniqueViewers))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: spacing.sm, marginBottom: spacing.lg }}>
                {stat('First viewed', fmtDateTime(data.firstViewedAt))}
                {stat('Last viewed', fmtDateTime(data.lastViewedAt))}
              </div>

              <div style={{ fontWeight: 600, fontSize: fontSize.base, color: colors.text, marginBottom: spacing.sm }}>Timeline</div>
              {data.events.length === 0 ? (
                <EmptyState title="Not viewed yet">{VIEWS_EMPTY_TEXT}</EmptyState>
              ) : (
                <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: `2px solid ${colors.border}`, marginLeft: 6 }}>
                  {data.events.map((ev, i) => (
                    <li key={`${ev.occurredAt}-${i}`} data-testid="view-event" style={{ position: 'relative', padding: `0 0 ${spacing.md} ${spacing.md}` }}>
                      <span style={{
                        position: 'absolute', left: -7, top: 3, width: 12, height: 12, borderRadius: '50%',
                        background: ev.event === 'download' ? colors.success : colors.primary, border: `2px solid ${colors.surface}`,
                      }} />
                      <div style={{ fontSize: fontSize.sm, color: colors.text, fontWeight: 500 }}>
                        {ev.event === 'download' ? 'Downloaded' : 'Viewed'} via {surfaceLabel(ev.surface)}
                      </div>
                      <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                        {fmtDateTime(ev.occurredAt)}
                      </div>
                      <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
                        {[capitalize(ev.device), ev.browser, ev.referrerDomain ? `from ${ev.referrerDomain}` : null].filter(Boolean).join(' · ')}
                        {ev.visitor && <> · visitor <span style={{ fontFamily: fontFamily.mono }}>{ev.visitor}</span></>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.md }}>
                Visitor ids are anonymous and rotate monthly. No IP addresses or locations are stored.
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
